import { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import { Eye, FileText } from "lucide-react";
import { startGetLeadFormByID } from "../../actions/adsCampaigns";
import {
  FORM,
  INSTANT_FORM_FIELD_DESCRIPTION,
  INSTANT_FORM_PREVIEW_MORE,
  INSTANT_FORM_SECTION_QUESTIONS,
  LOADING_INFORMATION,
  MODAL_VIEW_LEAD_FORM_TITLE,
  TITLE,
} from "../../i18n/keysTranslations";
import { useTranslationApp } from "../../lib/i18next";
import { useDispatchApp } from "../../lib/redux";
import {
  BORDER_GRAY_COLOR,
  GRAY_100,
  GRAY_200,
  GRAY_50,
  GRAY_500,
  PAPER_CONTRAST_TEXT,
} from "../../utils/colors";
import { BORDER_RADIUS } from "../../utils/constants";
import {
  buildLeadFormPreview,
  hasLeadFormPreviewContent,
  resolveLeadFormPreviewSource,
} from "../../utils/leadFormPreview";

const IDLE_STATE = {
  status: "idle",
  preview: null,
  formID: "",
};

const PreviewLabelValue = ({ labelKey, value }) => {
  const { t } = useTranslationApp();
  if (!value) return null;
  return (
    <Typography
      variant="caption"
      component="p"
      sx={{
        m: 0,
        color: GRAY_500,
        lineHeight: 1.45,
        display: "-webkit-box",
        WebkitLineClamp: 3,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
      }}
    >
      <Box
        component="span"
        sx={{ fontWeight: 600, color: PAPER_CONTRAST_TEXT }}
      >
        {t(labelKey)}
      </Box>
      {": "}
      {value}
    </Typography>
  );
};

const LeadFormSummaryCard = ({
  platform,
  form,
  recommendation,
  integrationID,
  pageID,
  adAccountID,
  onView,
}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const formRef = useRef(form);
  formRef.current = form;
  const recommendationRef = useRef(recommendation);
  recommendationRef.current = recommendation;
  const [state, setState] = useState(IDLE_STATE);
  const formID = form?.id || "";
  const name = form?.name || formID;
  const isCurrent = state.formID === formID;
  const status = !formID ? "idle" : isCurrent ? state.status : "loading";
  const preview = isCurrent ? state.preview : null;
  const viewLabel = t(MODAL_VIEW_LEAD_FORM_TITLE);
  const isClickable = typeof onView === "function";
  const showPreview =
    status === "loading" ||
    (status === "ready" && hasLeadFormPreviewContent(platform, preview));

  useEffect(() => {
    if (!formID) return undefined;

    let active = true;
    const localSource = resolveLeadFormPreviewSource(
      platform,
      formRef.current,
      recommendationRef.current
    );
    if (localSource) {
      const localPreview = buildLeadFormPreview(platform, localSource);
      setState({
        status: hasLeadFormPreviewContent(platform, localPreview)
          ? "ready"
          : "empty",
        preview: localPreview,
        formID,
      });
      return () => {
        active = false;
      };
    }

    const onLoad = async () => {
      const response = await dispatch(
        startGetLeadFormByID({
          platform,
          integrationID,
          pageID,
          adAccountID,
          leadFormID: formID,
          shouldContinue: () => active,
        })
      );
      if (!active || response?.code === "aborted") return;
      const source = response?.ok
        ? resolveLeadFormPreviewSource(
            platform,
            response.data,
            recommendationRef.current
          )
        : null;
      const nextPreview = source
        ? buildLeadFormPreview(platform, source)
        : null;
      setState({
        status:
          nextPreview && hasLeadFormPreviewContent(platform, nextPreview)
            ? "ready"
            : "empty",
        preview: nextPreview,
        formID,
      });
    };

    onLoad();

    return () => {
      active = false;
    };
  }, [
    adAccountID,
    dispatch,
    formID,
    integrationID,
    pageID,
    platform,
    recommendation,
  ]);

  const contactFields = (preview?.contactFields || []).filter(
    (field) => field?.labelKey
  );
  const showQuestions =
    contactFields.length > 0 || (preview?.moreCount ?? 0) > 0;

  return (
    <Box
      {...(isClickable && {
        component: "button",
        type: "button",
        onClick: onView,
        title: viewLabel,
        "aria-label": `${viewLabel}: ${name}`,
      })}
      sx={{
        width: "100%",
        p: 1.5,
        bgcolor: GRAY_50,
        borderRadius: BORDER_RADIUS,
        border: `1px solid ${BORDER_GRAY_COLOR}`,
        font: "inherit",
        textAlign: "left",
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        ...(isClickable && {
          cursor: "pointer",
          transition: "background-color 0.2s ease",
          "&:hover": {
            bgcolor: GRAY_100,
          },
          "&:focus-visible": {
            outline: `2px solid ${PAPER_CONTRAST_TEXT}`,
            outlineOffset: "2px",
          },
        }),
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 32,
            height: 32,
            borderRadius: 1,
            bgcolor: GRAY_200,
            flexShrink: 0,
          }}
        >
          <FileText size={16} />
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minWidth: 0,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: GRAY_500,
              fontWeight: 500,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            {t(FORM)}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              color: PAPER_CONTRAST_TEXT,
              overflowWrap: "anywhere",
            }}
          >
            {name}
          </Typography>
        </Box>
        {isClickable ? (
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              color: GRAY_500,
              fontSize: "0.8125rem",
              fontWeight: 500,
              flexShrink: 0,
            }}
          >
            <Eye size={16} />
            {viewLabel}
          </Box>
        ) : null}
      </Box>

      {showPreview ? (
        <Box
          sx={{
            mt: 1.25,
            pl: "44px",
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
            width: "100%",
          }}
        >
          {status === "loading" ? (
            <Box
              sx={{ display: "flex", py: 0.5 }}
              aria-busy="true"
              aria-label={t(LOADING_INFORMATION)}
            >
              <CircularProgress size={16} />
            </Box>
          ) : (
            <>
              <PreviewLabelValue labelKey={TITLE} value={preview.title} />
              <PreviewLabelValue
                labelKey={INSTANT_FORM_FIELD_DESCRIPTION}
                value={preview.description}
              />
              {showQuestions ? (
                <>
                  <Typography
                    variant="caption"
                    component="p"
                    sx={{
                      m: 0,
                      fontWeight: 600,
                      color: PAPER_CONTRAST_TEXT,
                      lineHeight: 1.4,
                    }}
                  >
                    {t(INSTANT_FORM_SECTION_QUESTIONS)}
                  </Typography>
                  {contactFields.map((field) => (
                    <Typography
                      key={field.labelKey}
                      variant="caption"
                      component="p"
                      sx={{
                        m: 0,
                        color: GRAY_500,
                        lineHeight: 1.4,
                      }}
                    >
                      - {t(field.labelKey)}
                    </Typography>
                  ))}
                  {preview.moreCount > 0 ? (
                    <Typography
                      variant="caption"
                      sx={{ color: GRAY_500, lineHeight: 1.4 }}
                    >
                      {t(INSTANT_FORM_PREVIEW_MORE, {
                        count: preview.moreCount,
                      })}
                    </Typography>
                  ) : null}
                </>
              ) : null}
            </>
          )}
        </Box>
      ) : null}
    </Box>
  );
};

export default LeadFormSummaryCard;
